import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { changeOrganization, createOrganization, generateInvitedToken, listOrganization } from '@elecmap/api/src/ram'
import { getAccountTypeDesc, getAccountTypeStyle, useOrganization } from '@/store/organization'
import useUserStore from '@/store/user'
import { useSpaceStore } from '@/store/space'
import type { IFormItem, IKeywords } from '@elecmap/elecmap-ui/lib/form/types'
import { notificationError } from '@elecmap/shared/src/utils/notification'

export function useOrganizationDropdown() {
  const userStore = useUserStore()
  const router = useRouter()
  const store = useOrganization()
  const { setUser } = userStore
  const { userInfo, isPersonalOrg, canManage } = storeToRefs(userStore)
  const { setCurrentOrganizationId } = store
  const { organization, organizationId, currentOrganization } = storeToRefs(store)

  const dropdown = ref<HTMLElement | null>(null)
  const popoverVisible = ref(false)
  const organizationPopoverVisible = ref(false)

  const inviteVisible = ref(false)
  const inviteToken = ref()
  const inviteTooltipVisible = ref(false)

  function goChangeOrganization(id: number) {
    popoverVisible.value = false
    organizationPopoverVisible.value = false
    changeOrganization(id).then(res => {
      localStorage.removeItem('organizationId')
      setCurrentOrganizationId(id, res.organizationType)
      setUser(res)
      localStorage.setItem('refresh', '1')
      useSpaceStore().navs = []
      router.replace('/workbench/index').then(() => {
        window.location.reload()
      })
    })
  }

  function handleInviteVisible(visible: boolean) {
    if (isPersonalOrg.value) return
    popoverVisible.value = false
    inviteVisible.value = visible
    if (visible) {
      generateInvitedToken().then((res: any) => {
        inviteToken.value = res
      })
    }
  }

  function goHome() {
    router.push('/')
  }

  function logout() {
    userStore.logout().then(() => {
      router.push('/')
      const ss = useSpaceStore()
      ss.orgSpaceList = []
      ss.orgFolderList = []
      ss.orgProjectList = []
    })
  }

  const settingVisible = ref(false)
  const settingType = ref<'account' | 'member' | 'seat' | 'enterprise'>('account')

  function goPersonalSettings() {
    settingVisible.value = true
    popoverVisible.value = false
    settingType.value = 'account'
  }

  function goEnterpriseSettings() {
    popoverVisible.value = false
    settingVisible.value = true
    settingType.value = 'enterprise'
  }

  function goSeatSettings() {
    popoverVisible.value = false
    settingVisible.value = true
    settingType.value = 'seat'
  }

  function goMemberSettings() {
    popoverVisible.value = false
    settingVisible.value = true
    settingType.value = 'member'
  }

  const createFormRef = ref()
  const createVisible = ref(false)
  const creatingModalVisible = ref(false)
  const createForm = ref<IKeywords>({})
  const createDefs = reactive<IFormItem[]>([
    {
      key: 'organizationName',
      label: '团队名称',
      placeholder: '请输入团队名称',
      type: 'Input',
      rules: [{ required: true, message: '请输入' }],
    },
    {
      key: 'nickname',
      label: '你的昵称',
      placeholder: '请输入昵称',
      type: 'Input',
    },
    {
      key: 'scale',
      label: '规模',
      placeholder: '请选择规模',
      type: 'Select',
    },
    {
      key: 'industry',
      label: '行业',
      placeholder: '请选择行业',
      type: 'Select',
    },
  ])

  const creating = ref(false)

  function openCreateModal() {
    popoverVisible.value = false
    organizationPopoverVisible.value = false
    createVisible.value = true
  }

  async function handleOkCreate() {
    if (creating.value) return
    try {
      await createFormRef.value.validate()
      creating.value = true
      createVisible.value = false
      creatingModalVisible.value = true
      const org: any = await createOrganization({
        organizationName: createForm.value.organizationName,
      })
      userStore.showInitialModal = true
      const data = await listOrganization()
      store.setOrganization(data || [])
      goChangeOrganization(org.id)
    } catch {
      if (creating.value) {
        notificationError('创建失败，请重试')
      }
    } finally {
      creating.value = false
      creatingModalVisible.value = false
    }
  }

  return {
    dropdown,
    popoverVisible,
    organizationPopoverVisible,
    userInfo,
    isPersonalOrg,
    canManage,
    organization,
    organizationId,
    currentOrganization,
    getAccountTypeDesc,
    getAccountTypeStyle,
    goChangeOrganization,
    inviteVisible,
    inviteToken,
    handleInviteVisible,
    inviteTooltipVisible,
    goHome,
    logout,
    settingVisible,
    settingType,
    goPersonalSettings,
    goEnterpriseSettings,
    goSeatSettings,
    goMemberSettings,
    createFormRef,
    createVisible,
    creatingModalVisible,
    createForm,
    createDefs,
    openCreateModal,
    handleOkCreate,
    userStore,
    store,
  }
}
